import { OrderedColumnsT } from 'business/definitions';
import { FormatedVizData } from './types';
import { isDictAllDataValueT } from 'helpers/eChartHelpers';

type ToNormalizeRows = {
    rows: ResponseDataT[];
    totalValue: number;
};

export function normalizeData(
    data: ResponseDataT[],
    orderedColumns: OrderedColumnsT,
): ResponseDataT[] {
    const { x, y } = orderedColumns;
    if (!x || !y) {
        return data;
    }
    const xKey = x?.key;
    const yKey = y.key;

    const groupedData = data.reduce((acc, current) => {
        const xValue = isDictAllDataValueT(current[xKey])
            ? current[xKey].value
            : current[xKey];
        const rawYValue = current[yKey];
        const yValue = rawYValue ? parseFloat(rawYValue.toString()) || 0 : 0;

        const toNormalizeRows: ToNormalizeRows = acc.get(xValue) ?? {
            rows: [],
            totalValue: 0,
        };
        acc.set(xValue, {
            rows: [...toNormalizeRows.rows, current],
            totalValue: toNormalizeRows.totalValue + yValue,
        });
        return acc;
    }, new Map<ResponseDataValueT, ToNormalizeRows>());

    return Array.from(groupedData.values()).flatMap((toNormalizeRows) => {
        const { rows, totalValue } = toNormalizeRows;
        return rows.map((row) => {
            const yValue = row[yKey];
            if (typeof yValue !== 'number') {
                return row;
            }
            return {
                ...row,
                [yKey]: (yValue || 0) / totalValue,
            };
        });
    });
}

export const normalizeDataWithContext = (
    context: FormatedVizData,
): FormatedVizData => {
    const {
        chartData,
        chartColumns,
        context: { settings, vizType },
    } = context;
    if (
        settings.isNormalized &&
        chartColumns.z &&
        ['area', 'horizontal_bar', 'bar', 'polar_bar'].includes(vizType)
    ) {
        const newChartData = normalizeData(chartData, chartColumns);
        const updatedYColumn: ResponseColumnT = {
            ...chartColumns.y,
            type: 'fraction',
        };
        const newChartColumns = {
            ...chartColumns,
            y: updatedYColumn,
        };
        return {
            ...context,
            chartData: newChartData,
            chartColumns: newChartColumns,
        };
    }
    return context;
};
